import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { useEffect, useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  postDownloadEntitesAction,
  postDownloadSelectedEntitesAction, postEmailEntitesAction,
  postEmailSelectedEntitesAction,
} from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM } from 'src/constants/Form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { t } from 'src/lib/language';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
}));

const DownloadTransactionsDialog = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.transactionDownload]?.opened || false);
  const selected =
    useSelector((state: Store) => state.selected[ENTITIES.fundraisingTransactions] || false);
  const count =
    useSelector(
      (state: Store) => (state.querybuilder[ENTITIES.fundraisingTransactions]
        && (state.querybuilder[ENTITIES.fundraisingTransactions].result?.count || 0)),
    );

  useEffect(() => {
    if (opened) {
      dispatch(setError({ [FORM.download]: {} }));
    }
  }, [opened]);

  const [selections, setSelections] = useState('all');

  const canDownload = () => {
    if (selections === 'selected') {
      if (selected.length > 1000) return false;
    }
    if (selections === 'result') {
      if (count > 1000) return false;
    }
    return true;
  };

  const [options, setOptions] = useState({
    file: 'Fundraising Transactions',
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.email,
    export_to_grid: false,
  } as DownloadOptionsModel);

  const extension = options.format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.transactionDownload }));
  };

  const handleDownload = () => {
    dispatch(setError({ [FORM.download]: {} }));
    if (!options.file) {
      dispatch(setError({
        [FORM.download]: {
          file: ['Required'],
        },
      }));
      return;
    }
    if (!options.file) {
      options.file = 'fundraising_transactions';
    }
    options.file = `${options.file.split('.')[0]}.${extension}`;
    if (options.delivery === DELIVERY_TYPE.download) {
      if (selections === 'all') {
        options.all = true;
        if (canDownload()) {
          dispatch(postDownloadEntitesAction(ENTITIES.fundraisingTransactions, options));
        } else {
          dispatch(postEmailEntitesAction(ENTITIES.fundraisingTransactions, options));
        }
      }
      if (selections === 'selected') {
        if (canDownload()) {
          dispatch(postDownloadSelectedEntitesAction(
            ENTITIES.fundraisingTransactions, selected, options,
          ));
        } else {
          dispatch(postEmailSelectedEntitesAction(
            ENTITIES.fundraisingTransactions, selected, options,
          ));
        }
      }
      if (selections === 'result') {
        if (canDownload()) {
          dispatch(postDownloadEntitesAction(ENTITIES.fundraisingTransactions, options));
        } else {
          dispatch(postEmailEntitesAction(ENTITIES.fundraisingTransactions, options));
        }
      }
    }
    if (options.delivery === DELIVERY_TYPE.email) {
      if (selections === 'all') {
        options.all = true;
        dispatch(postEmailEntitesAction(
          ENTITIES.fundraisingTransactions, options,
        ));
      }
      if (selections === 'selected') {
        dispatch(postEmailSelectedEntitesAction(
          ENTITIES.fundraisingTransactions, selected, options,
        ));
      }
      if (selections === 'result') {
        dispatch(postEmailEntitesAction(
          ENTITIES.fundraisingTransactions, options,
        ));
      }
    }
    dispatch(closeDialog({ dialog: DIALOGS.transactionDownload }));
  };

  const onChange = (value: Value, field: string) => {
    setOptions({
      ...options,
      [field]: value,
    });
  };

  return (
    <Dialog
      aria-labelledby="download-result"
      open={opened}>
      <DialogTitle id="download-result">Download Transaction(s)</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          <div className={classes.row}>
            <FormTextField
              form={FORM.download}
              label="File Name"
              name="file"
              onChange={onChange}
              required
              value={options.file.split('.')[0]} />
            { `.${extension}` }
          </div>
          <FormControl component="fieldset" style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Format</FormLabel>
            <RadioGroup
              row
              aria-label="format"
              name="format"
              value={options.format || FORMAT.pdf}
              onChange={(e) => onChange(e.target.value, 'format')}>
              <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
              <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Orientation</FormLabel>
            <RadioGroup
              row
              aria-label="orientation"
              name="orientation"
              value={options.orientation || ORIENTATION.portrait}
              onChange={(e) => onChange(e.target.value, 'orientation')}>
              <FormControlLabel
                value={ORIENTATION.portrait}
                control={<Radio />}
                label={ORIENTATION.portrait} />
              <FormControlLabel
                value={ORIENTATION.landscape}
                control={<Radio />}
                label={ORIENTATION.landscape} />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Download</FormLabel>
            <RadioGroup
              row
              aria-label="Selected"
              name="all"
              value={selections}
              onChange={(e) => setSelections(e.target.value)}>
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All" />
              <FormControlLabel
                value="result"
                control={<Radio />}
                label="Result" />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label="Selected" />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Delivery Type</FormLabel>
            <RadioGroup
              row
              aria-label="Delivery Type"
              name="download"
              value={options.delivery || DELIVERY_TYPE.email}
              onChange={(e) => onChange(e.target.value, 'delivery')}>
              <FormControlLabel
                value={DELIVERY_TYPE.email}
                control={<Radio />}
                label="By Email" />
              { canDownload() && (
                <FormControlLabel
                  value={DELIVERY_TYPE.download}
                  control={<Radio />}
                  label="Download" />
              )}
            </RadioGroup>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          { t(lang, 'misc.cancel') }
        </Button>
        <Button onClick={handleDownload} color="primary">
          { t(lang, 'misc.download') }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadTransactionsDialog;
